import React from 'react';
import { IMaterialToAccount, IRiskTolerance } from '@sp-types/accountTypes';
import { INetExposure } from '@sp-types/netExposureType';
import { getNetExposure } from '@utils/exposureUtils';
import { ExposureZone, generateDangerZone, HeaderSubLabel } from './MaterialDetailUtils';
import { InfoOutlined } from '@mui/icons-material';
import { LinearGauge } from '@UI-Elements/Charts/LinearGauge/LinearGauge';
import ButtonDesigned from '@UI-Elements/Buttons/ButtonDesigned';
import { FormatToShortPriceTemplate } from '../../../../utils/FormatValue';
const MILLION = 1_000_000;

interface RiskBounds {
  balanceFrom: number;
  balanceTo: number;
  warnFrom: number;
  warnTo: number;
}

interface Props {
  materialId: string;
  accountMaterial?: IMaterialToAccount;
  netExposure: INetExposure;
  convertionMap: Record<string, number>;
  onCancel: () => void;
  onSave: (_risk: IRiskTolerance) => void;
}
export const EditExposure: React.FC<Props> = ({
  materialId,
  accountMaterial,
  netExposure,
  convertionMap,
  onCancel,
  onSave,
}) => {
  const [riskBounds, setRiskBounds] = React.useState<RiskBounds>({
    balanceFrom: -6,
    balanceTo: 6,
    warnFrom: -15,
    warnTo: 15,
  });

  React.useEffect(() => {
    if (accountMaterial?.RiskTolerance) {
      const { dangerAreaMinus, dangerAreaPlus, stableArea } = accountMaterial.RiskTolerance;

      setRiskBounds({
        balanceFrom: stableArea.start / MILLION,
        balanceTo: stableArea.end / MILLION,
        warnFrom: dangerAreaMinus.end / MILLION,
        warnTo: dangerAreaPlus.start / MILLION,
      });
    }
  }, [accountMaterial]);

  const onSaveRisk = ({ balanceFrom, balanceTo, warnFrom, warnTo }: RiskBounds) => {
    const maxZone = generateDangerZone(warnFrom, warnTo);

    const riskTolerance: IRiskTolerance = {
      stableArea: { start: balanceFrom * MILLION, end: balanceTo * MILLION },
      dangerAreaMinus: { start: -maxZone * MILLION, end: warnFrom * MILLION },
      dangerAreaPlus: { end: maxZone * MILLION, start: warnTo * MILLION },
    };

    onSave(riskTolerance);
  };

  const exposure = 41;

  return (
    <div className="flex flex-col flex-1 gap-4 p-3 text-lg">
      <p className="flex text-lg">Edit exposure</p>
      <div className="flex border-1 border-gray-100 p-3 gap-20">
        <HeaderSubLabel
          line1="Current exposure"
          subline="Annualized STD"
          line2={FormatToShortPriceTemplate(getNetExposure(netExposure, convertionMap, materialId))}
        />
        <div>
          {accountMaterial && (
            <div className="w-80">
              <LinearGauge
                riskTolerance={accountMaterial.RiskTolerance}
                currentValue={getNetExposure(netExposure, convertionMap, materialId)}
              />
            </div>
          )}
        </div>
        <HeaderSubLabel border line1="Volatility risk" subline="Annual volatility" line2={exposure.toFixed(1) + '%'} />
        <HeaderSubLabel
          border
          line1="Potential loss"
          subline={
            <span className="flex">
              % value<p className="flex-1"></p>$ value
            </span>
          }
          line2={
            <span className="flex">
              +55% <p className="flex-1"></p> 0%
            </span>
          }
        />
        <InfoOutlined />
      </div>

      <div className="flex flex-1 gap-40">
        <ExposureZone
          title="1. Balance zone"
          above={riskBounds.balanceFrom}
          below={riskBounds.balanceTo}
          setAbove={(balanceFrom) => setRiskBounds((risk) => ({ ...risk, balanceFrom }))}
          setBelow={(balanceTo) => setRiskBounds((risk) => ({ ...risk, balanceTo }))}
        />
        <ExposureZone
          title="2. Warning zone"
          above={riskBounds.warnFrom}
          below={riskBounds.warnTo}
          setAbove={(warnFrom) => setRiskBounds((risk) => ({ ...risk, warnFrom }))}
          setBelow={(warnTo) => setRiskBounds((risk) => ({ ...risk, warnTo }))}
        />
        <ExposureZone
          title="3. Danger zone"
          above={riskBounds.warnTo}
          below={riskBounds.warnFrom}
          disabled
          setAbove={() => null}
          setBelow={() => null}
        />
      </div>

      <div className="flex flex-1 gap-4 justify-end">
        <ButtonDesigned style="outline" onButtonClick={onCancel} className="text-blue-600 w-24 py-2 mt-auto">
          Cancel
        </ButtonDesigned>
        <ButtonDesigned style="primary" onButtonClick={() => onSaveRisk(riskBounds)} className="w-24 py-2 mt-auto">
          Save
        </ButtonDesigned>
      </div>
    </div>
  );
};
